import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import type { NavGroup } from "../nav/navConfig.js";

export type UseSectionSpyOptions = {
  /** IntersectionObserver rootMargin band — defaults to the TocNav band
   *  ("-20% 0px -55% 0px"): the section crossing 20%–45% of the viewport wins. */
  rootMargin?: string;
};

const DEFAULT_ROOT_MARGIN = "-20% 0px -55% 0px";
const THRESHOLD = [0, 0.1, 0.25, 0.5, 0.75, 1];

/** The ids to newly attach to the IntersectionObserver on one pass, plus whether
 *  every requested id is now observed (so the MutationObserver can be released). */
export type AttachDecision = { attach: string[]; complete: boolean };

/**
 * Pure "attach newly found ids" decision, extracted so it can be unit-tested
 * without a DOM. Given the requested ids, the ids already observed, and a
 * presence lookup, it returns the ids to attach this pass (present in the DOM
 * and not already observed) and whether the requested set is now fully observed.
 * Never returns an already-observed id.
 */
export const selectIdsToAttach = (
  requestedIds: readonly string[],
  observed: ReadonlySet<string>,
  isPresent: (id: string) => boolean
): AttachDecision => {
  const attach: string[] = [];
  for (const id of requestedIds) {
    if (observed.has(id)) continue;
    if (isPresent(id)) attach.push(id);
  }
  const complete = requestedIds.every((id) => observed.has(id) || attach.includes(id));
  return { attach, complete };
};

/**
 * Scroll-spy: given the ids of on-page sections, returns the id currently in the
 * active band, or null when none intersects.
 *
 * Observes elements by id on the CURRENT page, re-observing when the id list or
 * the route changes. Targets can mount well after this hook runs — the Software
 * RAID and Hardware Health sections are gated on the first SSE sample, which can
 * arrive after any fixed budget. So after an initial rAF pass this hook holds a
 * `MutationObserver` on `document.body` and attaches each anchor to the SAME
 * `IntersectionObserver` the moment it appears, with no time cutoff; the
 * MutationObserver is released once every requested id is observed. An id that
 * never mounts (no RAID on this box) simply stays unobserved — no timers, no
 * polling. Cleanup-correct: unmount/route-change disconnects both observers.
 *
 * Read-only by design: it NEVER pushes history or rewrites the URL hash, so it
 * cannot spam the history stack or re-fire the shell hash-scroll hook.
 */
export const useSectionSpy = (ids: string[], opts?: UseSectionSpyOptions): string | null => {
  const location = useLocation();
  const [activeId, setActiveId] = useState<string | null>(null);
  // Latest hash + active id, read live inside the observer callback so the spy
  // resolves against current values without rebuilding the observer on every
  // hash change or active-id change.
  const hashRef = useRef(location.hash);
  hashRef.current = location.hash;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  // Stable primitive dep so the effect re-runs on a change to the id SET, not on
  // every render (callers pass a fresh array literal each render).
  const idsKey = ids.join("|");
  const rootMargin = opts?.rootMargin ?? DEFAULT_ROOT_MARGIN;

  useEffect(() => {
    const sectionIds = idsKey ? idsKey.split("|") : [];
    if (sectionIds.length === 0) {
      setActiveId(null);
      return;
    }

    const observed = new Set<string>();

    // The full set of ids currently in the band. An IntersectionObserver
    // callback carries only the entries that CHANGED, so we accumulate the set
    // across callbacks to resolve the active id among ALL intersecting sections
    // (a ratio-only pick highlighted a side-by-side neighbour that filled more
    // of the band than the section actually in view).
    const intersecting = new Set<string>();

    const topOf = (id: string): number => {
      const el = document.getElementById(id);
      return el ? el.getBoundingClientRect().top : Number.POSITIVE_INFINITY;
    };

    const isInViewport = (id: string): boolean => {
      const el = document.getElementById(id);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };

    const atBottom = (): boolean =>
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;

    const resolveActive = () => {
      const hashId = hashRef.current ? hashRef.current.slice(1) : "";

      // At maximum scroll the band (20%–45% of the viewport) can never reach the
      // last sections, so a section whose tail still touches the band would win
      // over the sections actually filling the lower viewport. When scrolled to
      // the end, resolve by viewport visibility instead of the band: hash
      // affinity still wins when its target is on screen, else the DEEPEST
      // visible section (last in DOM order that intersects the viewport at all).
      if (atBottom()) {
        if (hashId && isInViewport(hashId)) {
          setActiveId(hashId);
          return;
        }
        let deepest: string | null = null;
        for (const id of sectionIds) {
          if (isInViewport(id)) deepest = id;
        }
        if (deepest) {
          setActiveId(deepest);
          return;
        }
        // Nothing visible (should not happen at the bottom) → fall through.
      }

      // Candidates in DOM order (the ids array order), so a top tie breaks by it.
      const candidates = sectionIds.filter((id) => intersecting.has(id));
      if (candidates.length === 0) return; // nothing in band → keep the last (sticky)

      // (1) Hash affinity: the section the user just navigated to stays active
      //     while it is still in view, even if a neighbour fills more of the band.
      if (hashId && candidates.includes(hashId)) {
        setActiveId(hashId);
        return;
      }
      // (2) Stickiness: if the current active id is still in view, keep it — no
      //     flicker between two side-by-side sections.
      if (activeIdRef.current && candidates.includes(activeIdRef.current)) return;
      // (3) Otherwise the highest section wins (smallest top); an exact tie
      //     keeps the earlier candidate, i.e. DOM/ids-array order.
      let best = candidates[0]!;
      let bestTop = topOf(best);
      for (let i = 1; i < candidates.length; i += 1) {
        const id = candidates[i]!;
        const top = topOf(id);
        if (top < bestTop) {
          best = id;
          bestTop = top;
        }
      }
      setActiveId(best);
    };

    const onIntersect: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) intersecting.add(entry.target.id);
        else intersecting.delete(entry.target.id);
      }
      resolveActive();
    };

    const observer = new IntersectionObserver(onIntersect, { rootMargin, threshold: THRESHOLD });

    // Attach every requested id already in the DOM to the observer, and report
    // whether the full set is now observed. Never re-observes an id.
    const attachAvailable = (): boolean => {
      const { attach, complete } = selectIdsToAttach(
        sectionIds,
        observed,
        (id) => document.getElementById(id) !== null
      );
      for (const id of attach) {
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          observed.add(id);
        }
      }
      return complete;
    };

    let mutationObserver: MutationObserver | null = null;
    let attachRaf = 0;

    // Initial connect on the next frame (targets may not be in the DOM yet).
    let connectRaf = window.requestAnimationFrame(() => {
      connectRaf = 0;
      if (attachAvailable()) return;
      // Some anchors mount later (SSE-gated RAID / Hardware Health). Observe the
      // DOM and attach each the moment it appears — no time budget. Coalesced to
      // one lookup per frame; released once every id is observed.
      mutationObserver = new MutationObserver(() => {
        if (attachRaf) return;
        attachRaf = window.requestAnimationFrame(() => {
          attachRaf = 0;
          if (attachAvailable()) {
            mutationObserver?.disconnect();
            mutationObserver = null;
          }
        });
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    });

    // The observer alone will not fire at maximum scroll (nothing crosses a
    // threshold there), so re-resolve when the at-bottom state FLIPS. Passive,
    // rAF-debounced, and only acts on a flip so it costs nothing mid-scroll.
    let atBottomState = atBottom();
    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        const now = atBottom();
        if (now !== atBottomState) {
          atBottomState = now;
          resolveActive();
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (connectRaf) window.cancelAnimationFrame(connectRaf);
      if (attachRaf) window.cancelAnimationFrame(attachRaf);
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
      window.removeEventListener("scroll", onScroll);
      mutationObserver?.disconnect();
      observer.disconnect();
    };
  }, [idsKey, rootMargin, location.pathname, location.key]);

  return activeId;
};

/**
 * Collect the on-page hash-section ids (the part after `#`) for the nav group
 * whose `path` equals the current pathname. Feeds `useSectionSpy` so it observes
 * exactly the Quick-Nav children of the group the user is looking at.
 */
export const collectGroupHashIds = (
  sections: ReadonlyArray<{ groups?: NavGroup[] }>,
  pathname: string
): string[] => {
  const ids: string[] = [];
  const walk = (groups: NavGroup[]) => {
    for (const group of groups) {
      if (group.path === pathname) {
        for (const item of group.items) {
          const hashIdx = item.path.indexOf("#");
          if (hashIdx !== -1 && item.path.slice(0, hashIdx) === pathname) {
            ids.push(item.path.slice(hashIdx + 1));
          }
        }
      }
      if (group.groups) walk(group.groups);
    }
  };
  for (const section of sections) {
    if (section.groups) walk(section.groups);
  }
  return ids;
};
